import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import tracker from "./utils/sanctuary-tracker";
import { Toaster } from "react-hot-toast";
import Layout from "./components/Layout";
import ZenBackground from "./components/ZenBackground";
import Home from "./pages/Home";
import Login from "./pages/Login";
import SignupOnboarding from "./pages/SignupOnboarding";
import JoinGateway from "./pages/JoinGateway"; // NEW
import ForgotPassword from "./pages/ForgotPassword";
import VerifyEmail from "./pages/VerifyEmail"; // NEW
import VerifyPending from "./pages/VerifyPending";
import ResetPassword from "./pages/ResetPassword"; // NEW
import Notifications from "./pages/Notifications"; // NEW
import DashboardSeeker from "./seeker/dashboard/DashboardSeeker";
import DashboardProvider from "./provider/dashboard/DashboardProvider";
import Explore from "./pages/Explore"; 
// import Haven from "./pages/Haven"; // Removed for Light MVP
// import Haven from "./pages/Haven"; // Removed for Light MVP
import ChatSeeker from "./seeker/chat/ChatSeeker";
import ChatProviderWrapper from "./provider/chat/ChatProvider";
import RoleRoute from "./components/RoleRoute";
import ProfileSeeker from "./seeker/profile/ProfileSeeker";
import ProfileProvider from "./provider/profile/ProfileProvider";
import ArticleDetail from "./pages/ArticleDetail";
import CategoryDetail from "./pages/CategoryDetail";

import NotFound from "./pages/NotFound";
import About from "./pages/About";         // NEW
import Ethics from "./pages/Ethics";       // NEW
import Support from "./pages/Support";     // NEW
import Donate from "./pages/Donate";       // NEW
import Sustain from "./pages/Sustain";     // NEW (Sustain Page)
import Terms from "./pages/legal/Terms";   // LEGAL
import Privacy from "./pages/legal/Privacy"; // LEGAL
import Crisis from "./pages/legal/Crisis";   // LEGAL
// ADMIN
import AdminLayout from "./components/Admin/AdminLayout";
import AdminDashboard from "./pages/Admin/Dashboard";
import AdminAYI from "./pages/Admin/AYI";
import AdminUsers from "./pages/Admin/Users";
import AdminQuizManager from "./pages/Admin/QuizManager"; // NEW
import SafetyQueue from "./pages/Admin/SafetyQueue";
import ProviderVerification from "./pages/Admin/ProviderVerification";
import ArticleList from "./components/Admin/ArticleList";
import ArticleEditor from "./components/Admin/ArticleEditor";
import SecuritySettings from "./components/Admin/SecuritySettings";

// GATEKEEPER
import PublicApplication from "./pages/Apply";
// Old Gatekeeper pages removed
// import GatekeeperIntro from "./pages/Gatekeeper/Intro";
// import GatekeeperQuiz from "./pages/Gatekeeper/Quiz"; 
// import GatekeeperVideo from "./pages/Gatekeeper/Video";
// import GatekeeperStatus from "./pages/Gatekeeper/Status";

import RequireAuth from "./components/RequireAuth";
import { AuthProvider } from "./context/AuthContext"; // Import Auth Provider
import { ChatProvider } from "./context/ChatContext"; // Import ChatProvider
import { LanguageProvider } from "./context/LanguageContext"; // NEW
import CookieBanner from "./components/Legal/CookieBanner";

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    tracker.trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function UrlSanitizer() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // 🧘 Clean URLs from invisible characters (like %E2%80%89 thin space from mobile shares)
    const rawPath = location.pathname;
    const decodedPath = decodeURIComponent(rawPath);
    
    if (/[\u2000-\u200F\uFEFF\s]$/.test(decodedPath)) {
      const cleanPath = decodedPath.replace(/[\u2000-\u200F\uFEFF\s]+$/, '');
      // Use navigate with replace: true instead of window.location.reload()
      navigate(cleanPath + location.search, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

export default function App() {
  useEffect(() => {
    // 🧘 Baseline session initialization handled by tracker constructor
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <ChatProvider>
        <UrlSanitizer />
        <PageTracker />
        <CookieBanner />
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            style: { zIndex: 99999 }
          }}
          containerStyle={{
            zIndex: 99999
          }}
        />
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* Public */}
            <Route index element={<Home />} />
            <Route path="login" element={<Login />} />

            {/* 🔑 Signup Flow Split */}
            <Route path="signup" element={<JoinGateway />} />
            <Route path="signup-flow" element={<SignupOnboarding />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="verify-email" element={<VerifyEmail />} />
            <Route path="verify-pending" element={<VerifyPending />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="explore" element={<Explore />} />
            <Route path="wisdom/:id" element={<CategoryDetail />} />
            <Route path="article/:idOrSlug" element={<ArticleDetail />} />


            {/* Protected */}
            <Route
              path="dashboard"
              element={
                <RoleRoute
                  seekerComponent={DashboardSeeker}
                  providerComponent={DashboardProvider}
                />
              }
            />



            <Route
              path="chat"
              element={
                <RequireAuth>
                  <RoleRoute
                    seekerComponent={ChatSeeker}
                    providerComponent={ChatProviderWrapper}
                  />
                </RequireAuth>
              }
            />

            <Route
              path="notifications"
              element={
                <RequireAuth>
                  <Notifications />
                </RequireAuth>
              }
            />
            <Route
              path="profile"
              element={
                <RequireAuth>
                  <RoleRoute
                    seekerComponent={ProfileSeeker}
                    providerComponent={ProfileProvider}
                  />
                </RequireAuth>
              }
            />
            {/* 👁️ THE ALL-SEER (ADMIN PANEL) */}
            <Route path="admin" element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="ayi" element={<AdminAYI />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="safety" element={<SafetyQueue />} />
              <Route path="verify" element={<ProviderVerification />} />
              <Route path="quiz" element={<AdminQuizManager />} />
              <Route path="articles" element={<ArticleList />} />
              <Route path="articles/new" element={<ArticleEditor />} />
              <Route path="articles/:id" element={<ArticleEditor />} />
              <Route path="security" element={<SecuritySettings />} />
            </Route>
            {/* Public Content Pages */}
            <Route path="about" element={<About />} />
            <Route path="ethics" element={<Ethics />} />
            <Route path="support" element={<Support />} />
            <Route path="donate" element={<Donate />} />
            <Route path="sustain" element={<Sustain />} /> {/* New Sustain Route */}

            {/* 🛡️ NEW PUBLIC GATEKEEPER (Pre-Signup) */}
            <Route path="apply" element={<PublicApplication />} />

            {/* Legal & Crisis */}
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="crisis" element={<Crisis />} />

            {/* 404 - The Sanctuary */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        </ChatProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
// Force deploy final fix// Sanctuary Build Trigger: Sun May 10 06:36:10 PM IST 2026
